/**
 * BillingContext.tsx
 *
 * Provides subscription state and plan enforcement to the whole app.
 *
 * How it works:
 *  1. On mount, loads the `subscriptions` row for the default company.
 *  2. Exposes `subscription`, `plan`, and helper `canAccess(feature)`.
 *  3. `PLAN_FEATURES` maps each plan to the set of allowed ModuleKeys and
 *     the seat limit — used by BillingPage and module guards.
 *  4. `createCheckoutSession(priceId)` redirects to Stripe Checkout.
 *     The Stripe publishable key and price IDs come from environment variables.
 *  5. `openCustomerPortal()` redirects to Stripe Customer Portal so the
 *     user can manage / cancel their subscription without custom UI.
 *
 * Environment variables required (in .env.local):
 *   REACT_APP_STRIPE_PUBLISHABLE_KEY   — Stripe publishable key (pk_live_xxx or pk_test_xxx)
 *   REACT_APP_STRIPE_PRICE_STARTER     — Stripe Price ID for starter plan
 *   REACT_APP_STRIPE_PRICE_GROWTH      — Stripe Price ID for growth plan
 *   REACT_APP_STRIPE_PRICE_BUSINESS    — Stripe Price ID for business plan
 *
 * The actual Stripe session creation is done via a Supabase Edge Function
 * (`stripe-checkout`) so the secret key never touches the browser.
 *
 * In Phase 1 single-tenant mode all plan enforcement is client-side only
 * — the database subscription row is the source of truth and is written
 * by the webhook Edge Function after Stripe confirms payment.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
// Stripe.js is used for loading the publishable key.
// The actual redirect uses window.location.href with the URL returned by
// the Edge Function — `redirectToCheckout` was removed in stripe-js v3.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';
import { api, apiConfigured } from '../lib/apiClient';
import { canAccessModule } from '../lib/billing';
import { Subscription, BillingPlan, BillingEvent, ModuleKey } from '../types';
import { useWorkspaceId } from './AuthContext';

// ─── Plan feature matrix ──────────────────────────────────────────────────────

/**
 * Defines which modules are included on each plan.
 * Modules not listed are locked behind an upgrade.
 * `seats` is the maximum number of active profiles allowed (null = unlimited).
 */
export interface PlanFeatures {
  modules: ModuleKey[];
  seats: number | null;
  apiAccess: boolean;
  /** Display price in EUR/month (for the pricing page). */
  price: number;
  label: string;
}

export const PLAN_FEATURES: Record<BillingPlan, PlanFeatures> = {
  free: {
    label: 'Free',
    price: 0,
    seats: 10,
    apiAccess: false,
    modules: [],
  },
  starter: {
    label: 'Starter',
    price: 29,
    seats: 25,
    apiAccess: false,
    modules: ['hr', 'projects'],
  },
  growth: {
    label: 'Growth',
    price: 79,
    seats: 100,
    apiAccess: false,
    modules: ['hr', 'performance', 'projects', 'support', 'communication', 'analytics'],
  },
  business: {
    label: 'Business',
    price: 149,
    seats: 500,
    apiAccess: true,
    modules: ['hr', 'performance', 'projects', 'support', 'communication', 'finance', 'analytics', 'recruitment', 'crm'],
  },
  enterprise: {
    label: 'Enterprise',
    price: 0, // custom
    seats: null,
    apiAccess: true,
    modules: ['hr', 'performance', 'projects', 'support', 'communication', 'finance', 'analytics', 'recruitment', 'crm'],
  },
};

// ─── Context type ─────────────────────────────────────────────────────────────

interface BillingContextValue {
  /** True while the initial load is in progress. */
  loading: boolean;
  /** The current subscription row (null until loaded or if not found). */
  subscription: Subscription | null;
  /** Current plan key, defaults to 'free' if no subscription found. */
  plan: BillingPlan;
  /** Feature flags for the current plan. */
  features: PlanFeatures;
  /** Recent billing events / invoices. */
  invoices: BillingEvent[];
  /**
   * Returns true if the given module is accessible on the current plan.
   * Also returns true if the subscription has a module_override that explicitly
   * enables it (used for add-on modules sold outside a bundle).
   */
  isPlanModule: (key: ModuleKey) => boolean;
  /**
   * Redirect to Stripe Checkout for the given price ID.
   * Uses the Supabase `stripe-checkout` Edge Function to create the session
   * so the Stripe secret key is never exposed to the browser.
   */
  createCheckoutSession: (priceId: string) => Promise<void>;
  /**
   * Redirect to Stripe Customer Portal.
   * Allows the user to update payment method, cancel, or change plan.
   */
  openCustomerPortal: () => Promise<void>;
  /** Force reload subscription data (e.g. after returning from Stripe). */
  reload: () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const BillingContext = createContext<BillingContextValue>({
  loading:              true,
  subscription:         null,
  plan:                 'free',
  features:             PLAN_FEATURES['free'],
  invoices:             [],
  isPlanModule:         () => false,
  createCheckoutSession: async () => {},
  openCustomerPortal:   async () => {},
  reload:               async () => {},
});

// ─── Stripe loader (lazy) ─────────────────────────────────────────────────────

/** Whether Stripe is configured (publishable key present). */
function stripeConfigured() {
  return Boolean(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { workspaceId, settled } = useWorkspaceId();
  const [loading, setLoading]         = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices]       = useState<BillingEvent[]>([]);

  const load = useCallback(async () => {
    // Whose subscription? Unknown until the profile lands. Reading with no workspace would
    // return nothing, which reads as "you are on the free plan" and would lock a paying
    // company out of its own modules for the first moment after signing in.
    if (!workspaceId) {
      if (settled) setLoading(false);
      return;
    }
    try {
      // Phase-2 path: pull both subscription AND events via FastAPI when
      // the backend is reachable. Subscription endpoint shipped in v6.14;
      // events endpoint shipped in v6.17. Both are read-only and respect
      // the workspace_id from the JWT, so there's no leak risk.
      if (apiConfigured()) {
        const [subRes, evtRes] = await Promise.all([
          api.get<Subscription | null>('/billing/subscription'),
          api.get<BillingEvent[]>('/billing/events?limit=24'),
        ]);

        if (subRes.ok) {
          if (subRes.data) setSubscription(subRes.data);
          if (evtRes.ok) {
            setInvoices(evtRes.data ?? []);
            return;
          }
          // Subscription succeeded but events didn't — fall through so
          // the Supabase read populates events but we don't double-set
          // the subscription.
          console.warn(
            '[BillingContext] backend events read failed (status=%d): %s — falling back to Supabase events',
            evtRes.status,
            evtRes.error,
          );
          const invRes = await supabase
            .from('billing_events')
            .select('*')
            .eq('workspace_id', workspaceId)
            .order('created_at', { ascending: false })
            .limit(24);
          if (invRes.data) setInvoices(invRes.data as BillingEvent[]);
          return;
        }

        console.warn(
          '[BillingContext] backend subscription read failed (status=%d): %s — falling back',
          subRes.status,
          subRes.error,
        );
      }

      // Phase-1 fallback: full Supabase read of both tables.
      // eslint-disable-next-line no-restricted-syntax -- Promise.all results: error handling verified in surrounding code
      // eslint-disable-next-line no-restricted-syntax -- Promise.all results: error handling verified in surrounding code
      const [subRes, invRes] = await Promise.all([
        supabase
          .from('subscriptions')
          .select('*')
          .eq('workspace_id', workspaceId)
          .maybeSingle(),
        supabase
          .from('billing_events')
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false })
          .limit(24),
      ]);

      if (subRes.data) setSubscription(subRes.data as Subscription);
      if (invRes.data) setInvoices(invRes.data as BillingEvent[]);
    } catch (err) {
      console.warn('BillingContext load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, settled]);

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 5000);
    load().finally(() => clearTimeout(timeout));
    return () => clearTimeout(timeout);
  }, [load]);

  const plan: BillingPlan = (subscription?.plan as BillingPlan) ?? 'free';
  const features           = PLAN_FEATURES[plan];

  /**
   * Check if a module is unlocked on the current plan (or via add-on
   * override). Pure logic lives in `src/lib/billing.ts::canAccessModule`
   * so it's testable and so route guards / conditional UI can
   * compute the same answer outside the Context.
   */
  const isPlanModule = useCallback((key: ModuleKey): boolean => {
    const overrides = subscription?.module_overrides as Record<string, boolean> | null | undefined;
    return canAccessModule(features, overrides, key);
  }, [features, subscription]);

  /**
   * Create a Stripe Checkout session via the Supabase Edge Function
   * and redirect the browser to Stripe's hosted checkout page.
   */
  const createCheckoutSession = useCallback(async (priceId: string) => {
    if (!stripeConfigured()) {
      console.warn('Stripe not configured — set REACT_APP_STRIPE_PUBLISHABLE_KEY');
      return;
    }

    const success_url = `${window.location.origin}/admin/billing?success=1`;
    const cancel_url  = `${window.location.origin}/admin/billing?canceled=1`;

    // Phase-2 path: FastAPI handles Stripe so the secret key stays
    // server-side. The frontend just needs the hosted-page URL.
    if (apiConfigured()) {
      const res = await api.post<{ url: string; session_id: string }>(
        '/billing/checkout',
        { price_id: priceId, success_url, cancel_url },
      );
      if (res.ok) {
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        console.warn('[billing] backend checkout returned 2xx without url');
      } else {
        console.warn(
          '[billing] backend checkout failed (status=%d): %s — falling back to Supabase function',
          res.status,
          res.error,
        );
      }
    }

    // Phase-1 fallback: Supabase Edge Function. Kept until every
    // workspace has migrated.
    // No workspace = nothing to bill. Sending a guessed company id here would create a
    // Stripe checkout against the wrong company — a mistake with money attached.
    if (!workspaceId) {
      console.warn('[billing] checkout blocked — no workspace resolved yet');
      return;
    }

    const { data, error } = await supabase.functions.invoke('stripe-checkout', {
      body: {
        price_id:    priceId,
        company_id:  workspaceId,
        success_url,
        cancel_url,
      },
    });

    if (error || !data?.url) {
      console.error('Checkout session creation failed:', error);
      return;
    }

    window.location.href = data.url;
  }, [workspaceId]);

  /**
   * Open the Stripe Customer Portal so the user can manage their subscription
   * without us needing to build a full billing management UI.
   */
  const openCustomerPortal = useCallback(async () => {
    const return_url = `${window.location.origin}/admin/billing`;

    if (apiConfigured()) {
      const res = await api.post<{ url: string }>('/billing/portal', { return_url });
      if (res.ok) {
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        console.warn('[billing] backend portal returned 2xx without url');
      } else {
        console.warn(
          '[billing] backend portal failed (status=%d): %s — falling back to Supabase function',
          res.status,
          res.error,
        );
      }
    }

    const { data, error } = await supabase.functions.invoke('stripe-portal', {
      body: {
        customer_id: subscription?.stripe_customer_id,
        return_url,
      },
    });

    if (error || !data?.url) {
      console.error('Customer portal session creation failed:', error);
      return;
    }

    window.location.href = data.url;
  }, [subscription]);

  return (
    <BillingContext.Provider value={{
      loading, subscription, plan, features, invoices,
      isPlanModule, createCheckoutSession, openCustomerPortal, reload: load,
    }}>
      {children}
    </BillingContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Access billing state and plan information from any component.
 *
 * @example
 * const { plan, isPlanModule } = useBilling();
 * if (!isPlanModule('finance')) return <UpgradePrompt />;
 */
export function useBilling() {
  return useContext(BillingContext);
}
